import { Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { UnitStatus, WeatherStatus } from 'src/graphql/generated/graphql.generated';
import { CardViewModel } from 'src/models/card-view-model';
import { OperationViewModel } from 'src/models/operation-view-model';
import { RegionViewModel } from 'src/models/region-view-model';
import { selectUnitPositionsByUnitId } from 'src/store/entityStores/unit-position-selectors';
import { selectVehicleByUnitId } from 'src/store/entityStores/vehicle-unit-selectors';
import { UnitActivationRuleRecord } from 'src/store/generated/unit-activation-rule-record.generated';
import { UnitProps, UnitViewModelBase } from 'src/store/generated/unit-record.generated';
import { VehicleRecord } from 'src/store/generated/vehicle-record.generated';
import { unitNumberStr } from 'src/util/helpers/unit-helper';
import { undefinedOrMap, undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { humanize } from 'src/util/text';
import { getOrElse } from 'src/util/values';
import { BaseViewModel } from './base-view-model';
import { TimezoneViewModel } from './timezone-view-model';
import { VehicleViewModel } from './vehicle-view-model';

const statusConfig: { [status in UnitStatus]: { displayStatus?: string } } = {
  Available: {},
  Committed: {},
  OnStandby: {},
  OutOfService: { displayStatus: 'OOS' },
  PartiallyAvailable: {},
};

export const weatherStatusConfig: { [key in WeatherStatus]: { icon: string } } = {
  Green: {
    icon: 'cloud_done',
  },
  Red: {
    icon: 'cloud_off',
  },
  Yellow: {
    icon: 'cloud',
  },
  Unspecified: {
    icon: 'cloud_queue',
  },
};

export class UnitViewModel extends UnitViewModelBase {
  public readonly activeCard$: Observable<CardViewModel | undefined>;
  public readonly vehicle$: Observable<VehicleRecord | undefined>;
  public readonly base$: Observable<BaseViewModel | undefined>;
  public readonly baseTimezone$: Observable<TimezoneViewModel | undefined>;
  public readonly unitPositionIds$: Observable<Set<string>>;
  public readonly etlo$: Observable<number | undefined>;
  public readonly activationRule$: Observable<UnitActivationRuleRecord | undefined>;

  get config() {
    return statusConfig[this.status];
  }

  get statusDisplay() {
    return getOrElse(this.config.displayStatus, humanize(this.status));
  }

  get activeOperation$(): Observable<OperationViewModel | undefined> {
    return this.activeCard$.pipe(
      switchMap((card) => {
        if (card != null) {
          return card.operation$;
        }
        return of(undefined as OperationViewModel | undefined);
      }),
    );
  }

  get statusRollupDisplay$(): Observable<string> {
    return combineLatest([
      this.activeOperation$,
      this.activeOperation$.pipe(
        switchMap((op) => (op == null ? of(undefined) : op.trip$)),
        switchMap((trip) => (trip == null ? of(undefined) : trip.currentOrNextTripLeg$)),
      ),
    ]).pipe(
      debounceTime(10),
      map(([operation, currentOrNextLeg]) => {
        const vehicleDisposition = this.vehicle?.vehicleDisposition;
        if (vehicleDisposition != null) {
          const waypoint = vehicleDisposition.currentWaypoint;
          if (waypoint != null) {
            const status = vehicleDisposition.isEnRoute
              ? vehicleDisposition.statusDisplay
              : operation != null && operation.isOnStandby
              ? operation.statusDisplay
              : this.statusDisplay;
            return `${status} at ${waypoint.displayType}`;
          } else {
            const status = vehicleDisposition.isEnRoute
              ? vehicleDisposition.statusDisplay
              : this.statusDisplay;
            const destination =
              operation == null
                ? ''
                : currentOrNextLeg == null || currentOrNextLeg.endingWaypoint == null
                ? ` ${operation.statusDisplay}`
                : ` ${currentOrNextLeg.endingWaypoint.displayType}`;
            return `${status}${destination}`;
          }
        }
        return this.statusDisplay;
      }),
    );
  }

  get unitNumberStr() {
    return unitNumberStr(this);
  }

  constructor(
    unit: UnitProps,
    public readonly region: RegionViewModel,
    public readonly vehicle: VehicleViewModel | undefined,
    private readonly _model: ViewModelService,
  ) {
    super(unit);
    this.activeCard$ = _model.getActiveCardForUnit(this.id);
    this.vehicle$ = _model.store.select(selectVehicleByUnitId(this.id));
    this.base$ = _model.getBaseById(this.baseId);
    this.baseTimezone$ = this.base$.pipe(
      undefinedOrMap((b) => b.location),
      undefinedOrSwitchMap((l) => l.timezone$),
    );

    const baseEtlo$ = this.base$.pipe(
      map((base) =>
        base == null || base.estimatedLiftoffTime == null ? undefined : base.estimatedLiftoffTime,
      ),
    );
    this.etlo$ = unit.etloInMinutes == null ? baseEtlo$ : of(unit.etloInMinutes);

    this.unitPositionIds$ = _model.store
      .select(selectUnitPositionsByUnitId(this.id))
      .pipe(map((positions) => positions.map((ts) => ts.id).toSet()));
  }
}
